import type { NavigateFunction } from 'react-router-dom';
import { navigateToPartnerRedirect } from './safeRedirect';
import { getPartner, setPartnerInfo } from './storage';

type PartnerRedirectSource = {
    partnerRedirectUrl?: unknown;
    redirectUrl?: unknown;
    partnerInfo?: unknown;
    account?: unknown;
} | null | undefined;

function readPartnerRedirectUrl(source: PartnerRedirectSource): string | undefined {
    const url = source?.partnerRedirectUrl ?? source?.redirectUrl;
    return typeof url === 'string' && url.length > 0 ? url : undefined;
}

export function readPartnerInfo(value: unknown): Record<string, unknown> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    return value as Record<string, unknown>;
}

function asRedirectSource(value: unknown): PartnerRedirectSource {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    return value as PartnerRedirectSource;
}

export function readPartnerRedirectUrlFromResponse(data: unknown): string | undefined {
    const source = asRedirectSource(data);
    return readPartnerRedirectUrl(source) ?? readPartnerRedirectUrl(asRedirectSource(source?.account));
}

export function persistPartnerRedirectUrl(data: unknown): void {
    const partnerRedirectUrl = readPartnerRedirectUrlFromResponse(data);
    if (!partnerRedirectUrl) {
        return;
    }
    setPartnerInfo({
        ...(getPartner() ?? {}),
        partnerRedirectUrl,
    });
}

export function readAccountId(account?: { id?: unknown } | null): string | number | undefined {
    const id = account?.id;
    if (typeof id === 'number' && Number.isFinite(id)) {
        return id;
    }
    if (typeof id === 'string' && id.length > 0) {
        return id;
    }
    return undefined;
}

export function resolvePartnerRedirectUrl(
    account?: PartnerRedirectSource,
    partner?: PartnerRedirectSource
): string | undefined {
    return (
        readPartnerRedirectUrlFromResponse(account) ?? readPartnerRedirectUrlFromResponse(partner)
    );
}

export function resolvePartnerInfo(
    account?: PartnerRedirectSource,
    partner?: PartnerRedirectSource
): Record<string, unknown> | undefined {
    return readPartnerInfo(account?.partnerInfo) ?? readPartnerInfo(partner?.partnerInfo);
}

function toSearchParamValue(value: unknown): string | undefined {
    if (value == null) {
        return undefined;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return undefined;
        }
    }
    return String(value);
}

export function appendPartnerInfoSearchParams(
    url: string,
    partnerInfo?: Record<string, unknown> | null
): string {
    const info = readPartnerInfo(partnerInfo);
    if (!info) {
        return url;
    }

    const entries = Object.entries(info)
        .map(([key, value]) => [key, toSearchParamValue(value)] as const)
        .filter(([key, value]) => Boolean(key) && value !== undefined);

    if (entries.length === 0) {
        return url;
    }

    try {
        const parsed = new URL(url);
        for (const [key, value] of entries) {
            parsed.searchParams.set(key, value as string);
        }
        return parsed.toString();
    } catch {
        return url;
    }
}

export function redirectToPartnerOrConditions(
    navigate: NavigateFunction,
    redirectUrl?: string | null,
    partnerInfo?: Record<string, unknown> | null
): void {
    if (!redirectUrl) {
        navigate('/conditions');
        return;
    }
    navigateToPartnerRedirect(appendPartnerInfoSearchParams(redirectUrl, partnerInfo), {
        onBlocked: () => navigate('/conditions'),
    });
}
